import { CloseButton } from "@components/buttons/close";
import { PlusButton } from "@components/buttons/plus";
import {
  SHButton,
  SHDialog,
  SHStack,
  SHTable,
  SHTableBody,
  SHTableCell,
  SHTableHead,
  SHTableRow,
  SHTypography,
} from "@components/design-systems";
import { DELAY_TIME } from "@constants";
import { GlobalProductNames, GlobalSubProductNames } from "@constants/products";
import { useInvestment } from "@hooks/useInvestment";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  InvestmentSelectionDTO,
  PortfolioInvestmentTypeSelection,
  PortfolioTemplateFeeCalculatorDataDTO,
  SelectedInvestmentDTO,
} from "@models/managed-accounts/entities/step/setup";
import {
  AnalysisFeePortfolioDTO,
  AnalysisFeePortfolioSetupDTO,
  FeeCalculatorDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import {
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import shadows from "@mui/material/styles/shadows";
import { SelectInvestments } from "@pages/managed-accounts/components/auto-completes/investments";
import { SelectInvestmentsFavourited } from "@pages/managed-accounts/components/auto-completes/investments-favourited";
import { PortfolioTemplate } from "@pages/managed-accounts/components/auto-completes/portfolio-template";
import { SaveInvestmentAsTemplateDialog } from "@pages/managed-accounts/components/dialogs/save-as-template";
import { mapPortfolioFeeCalculatorsWithTemplates } from "@pages/managed-accounts/components/dialogs/save-as-template/util";
import { PortfolioSetupModalSkeleton } from "@pages/platform-analysis-v2/_id/components/skeletons/portfolio-setup-modal";
import { shouldHideSubProductName } from "@pages/platform-analysis/util";
import { hexToRGBA } from "@utils";
import {
  cloneDeep,
  concat,
  filter,
  findIndex,
  forEach,
  isEmpty,
  isEqual,
  omit,
  sortBy,
} from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useToggle } from "react-use";
import { AnalysisFeeCalculatorStepConfigs as StepConfigs } from "../../../../configs";
import { PortfolioSetupTableWrapper } from "../table-wrapper";
import {
  BalanceAvailabilitiesParams,
  getCreateFeeCalculatorsByVariables,
  getCreatePortfolioFeeCalculatorData,
  getEditFeeCalculatorsByVariables,
  getValidateBalanceResultForEdit,
  mapFeeCalculatorDataForTemplateReplacement,
  mapFeeCalculatorsDataToFeeCalculator,
  mapFeeCalculatorsToFeeCalculatorsData,
  mapInvestmentPortfolioForEdit,
} from "../utils";
import { FeeCalculatorRow } from "./fee-calculator-row";
import { AnalysisFeePortfolioSetupInvestmentRow } from "./investment-row";
import { AnalysisFeePortfolioSetupRemainingCalculator } from "./remaining-calculator";

const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    paddingRight: "0px",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
    {
      marginTop: "-1px",
    },
}));

type AnalysisFeePortfolioSetupModalProps = {
  type?: "create" | "edit";
  analysisId: string;
  open: boolean;
  calculatedTotal: {
    account: number;
    total: number;
    idps: number;
    super: number;
  };
  showFeeCalculatorSection: PortfolioInvestmentTypeSelection;
  portfolioSetup?: AnalysisFeePortfolioSetupDTO;
  portfolioSetupIndex?: number;
  portfolio?: AnalysisFeePortfolioDTO;
  feeCalculators?: FeeCalculatorDTO[];
  hasIDPSBalance?: boolean;
  hasSuperBalance?: boolean;

  onNewPortfolioCreated?: (
    createdPortfolio: AnalysisFeePortfolioDTO,
    setupIndex: number,
  ) => void;
  onOverwritePortfolio?: (updatedPortfolio?: AnalysisFeePortfolioDTO) => void;
  onClose: () => void;
};

export const AnalysisFeePortfolioSetupModal = ({
  type,
  analysisId,
  open,
  portfolioSetup,
  portfolioSetupIndex,
  portfolio,
  feeCalculators,
  calculatedTotal,
  hasIDPSBalance = calculatedTotal.idps > 0,
  hasSuperBalance = calculatedTotal.super > 0,
  showFeeCalculatorSection,
  onNewPortfolioCreated,
  onOverwritePortfolio,
  onClose,
}: AnalysisFeePortfolioSetupModalProps) => {
  const { palette } = useTheme();
  const {
    investmentSelections,
    originalInvestmentSelections,
    setInvestmentSelection,
  } = useInvestment();
  const {
    createNewPlatformAnalysisFeePortfolio,
    updatePlatformAnalysisFeePortfolio,
  } = usePlatformAnalysis();

  //State
  const [showDirtyPopup, toggleShowDirtyPopup] = useToggle(false);
  const [initialFormData, setInitialFormData] =
    useState<AnalysisFeePortfolioDTO>({
      ...new AnalysisFeePortfolioDTO(),
      id: portfolio?.id || "new",
      name: portfolio?.name || "",
      selectedTemplateId: portfolio?.selectedTemplateId || null,
    });
  const [initialInvestmentSelection, setInitialInvestmentSelection] = useState<
    InvestmentSelectionDTO[]
  >([]);
  const [showSaveAsTemplateDialog, setShowSaveAsTemplateDialog] =
    useState(false);

  // Form setup
  const formMethod = useForm<AnalysisFeePortfolioDTO>({
    mode: "onChange",
    defaultValues: {
      ...new AnalysisFeePortfolioDTO(),
      id: portfolio?.id || "new",
      name: portfolio?.name || "",
      selectedTemplateId: portfolio?.selectedTemplateId || null,
    },
  });
  const { control, getValues, watch } = formMethod;
  const { fields, remove, replace } = useFieldArray({
    control,
    name: "selectedInvestments",
  });

  const feeCalculatorData = watch("feeCalculatorData") || [];
  const selectedFeeCalculators = mapFeeCalculatorsDataToFeeCalculator(
    feeCalculatorData,
    feeCalculators ?? [],
  );
  const selectedInvestments = watch("selectedInvestments") || [];

  // Loading states
  const [isTemplateLoading, toggleIsTemplateLoading] = useToggle(true);
  const [isInvestmentLoading, toggleIsInvestmentLoading] = useToggle(true);
  const [isFavouritedInvestmentLoading, toggleIsFavouritedInvestmentLoading] =
    useToggle(true);
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const inputRef = useRef<HTMLInputElement>();

  // region Investment favourited management
  const [favouritedInvestments, setFavouritedInvestments] = useState<
    InvestmentSelectionDTO[] | undefined
  >();
  // endregion

  // region Validation states
  const [remainingValid, toggleRemainingValid] = useToggle(false);
  const [allocationValid, toggleAllocationValid] = useToggle(false);
  const [isMissingInvestment, toggleIsMissingInvestment] = useToggle(false);
  const [templateRemainingValid, toggleTemplateRemainingValid] =
    useToggle(false);
  const [investmentWeightValid, toggleInvestmentWeightValid] = useToggle(false);
  // endregion

  const isLoading = useMemo(
    () =>
      isTemplateLoading || isInvestmentLoading || isFavouritedInvestmentLoading,
    [isFavouritedInvestmentLoading, isInvestmentLoading, isTemplateLoading],
  );

  const isSaveButtonDisabled = useMemo(
    () =>
      isSubmitting ||
      !remainingValid ||
      !allocationValid ||
      isMissingInvestment,
    [isSubmitting, remainingValid, allocationValid, isMissingInvestment],
  );

  const isSaveTemplateButtonDisabled = useMemo(
    () => isSubmitting || !(templateRemainingValid && investmentWeightValid),
    [isSubmitting, templateRemainingValid, investmentWeightValid],
  );

  const handleOnModalClose = () => {
    const formData = cloneDeep(getValues());

    const fieldsToIgnore = ["belowMinimumInvestmentSizeTooltip"];
    const hasUpdateFormData = !isEqual(
      omit(formData, fieldsToIgnore),
      omit(initialFormData, fieldsToIgnore),
    );

    const hasUpdateInvestmentSelections = !isEqual(
      initialInvestmentSelection,
      investmentSelections,
    );

    const isDirty = hasUpdateFormData || hasUpdateInvestmentSelections;

    if (isDirty) {
      toggleShowDirtyPopup(true);
    } else {
      onClose();
    }
  };

  const filterFavouritedInvestments = (investments: InvestmentSelectionDTO[]) =>
    filter(investments, { isFavourited: true });

  const handleOnSelectInvestmentLoaded = (
    selections?: InvestmentSelectionDTO[],
  ) => {
    setInitialInvestmentSelection(selections ?? []);
    const favouritedInvestmentsFiltered = filterFavouritedInvestments(
      selections || [],
    );
    setFavouritedInvestments(favouritedInvestmentsFiltered);
  };

  const handleOnInvestmentRowFavouritedValueChange = (
    item: SelectedInvestmentDTO,
    isFavourited: boolean,
  ) => {
    if (!investmentSelections) return;

    const updatedInvestmentSelections = investmentSelections.map((investment) =>
      investment.shCode === item.shCode
        ? { ...investment, isFavourited }
        : investment,
    );

    const favouritedInvestmentsFiltered = filterFavouritedInvestments(
      updatedInvestmentSelections,
    );

    setInvestmentSelection(updatedInvestmentSelections);
    setFavouritedInvestments(favouritedInvestmentsFiltered);
  };

  const handleOnSavePortfolio = async () => {
    let formData = cloneDeep(getValues());
    formData = getCreatePortfolioFeeCalculatorData({
      data: formData,
      feeCalculators: feeCalculators ?? [],
      investments: investmentSelections,
      total: calculatedTotal.total,
    });

    const createdPortfolio = await createNewPlatformAnalysisFeePortfolio(
      analysisId,
      formData,
    );

    if (!createdPortfolio || portfolioSetupIndex === undefined) return;
    onNewPortfolioCreated &&
      onNewPortfolioCreated(createdPortfolio, portfolioSetupIndex);
    onClose();
  };

  const handleOnOverwritePortfolio = async () => {
    let formData = cloneDeep(getValues());
    formData = getCreatePortfolioFeeCalculatorData({
      data: formData,
      feeCalculators: feeCalculators ?? [],
      investments: investmentSelections,
      total: calculatedTotal.total,
    });

    const updatedPortfolio = await updatePlatformAnalysisFeePortfolio(
      analysisId,
      formData,
    );

    if (!updatedPortfolio) return;
    onOverwritePortfolio?.(updatedPortfolio);
    onClose();
  };

  const handleAppendInvestment = (investment: SelectedInvestmentDTO) => {
    let newArray = sortBy(concat(selectedInvestments, investment), [
      "managerName",
      "name",
    ]);

    newArray = newArray?.map((item) => {
      const { availableInSuper, availableInIDPS, weight } = item || {};
      const { balanceAvailability, isUnavailable } =
        getValidateBalanceResultForEdit({
          availableSuper: !!availableInSuper,
          availableIDPS: !!availableInIDPS,
          hasSuper: hasSuperBalance,
          hasIDPS: hasIDPSBalance,
          hasWeight: Number(weight) > 0,
        });

      return { ...item, balanceAvailability, isUnavailable };
    });

    replace(newArray);
  };

  const handleRemoveInvestment = (shCode: string) => {
    const indexToRemove = findIndex(fields, { shCode });
    remove(indexToRemove);
  };

  const handleRemoveFeeCalculator = (index: number) => {
    if (index < 0 || index >= feeCalculatorData.length) return;

    const newValue = feeCalculatorData.filter((_, i) => i !== index);
    formMethod.setValue("feeCalculatorData", newValue);
  };

  const handleRemoveAllInvestments = () => {
    const newFields = filter(fields, { isMissingInvestment: true });
    replace(newFields);
  };

  const handleRemoveAllFavouritedInvestments = () => {
    const arrIndex: number[] = [];
    const filteredItems = filter(selectedInvestments, { isFavourited: true });

    forEach(filteredItems, (item) => {
      const index = findIndex(fields, { shCode: item.shCode });
      arrIndex.push(index);
    });

    if (!isEmpty(arrIndex)) {
      remove(arrIndex);
    }
  };

  // region Templates handlers
  const handleTemplateReplaceInvestments = (
    investmentsToReplace: SelectedInvestmentDTO[],
  ) => {
    const mappedInvestments = mapInvestmentPortfolioForEdit({
      selectedInvestments: investmentsToReplace,
      allInvestments: investmentSelections,
      total: calculatedTotal.total,
      hasSuperBalance,
      hasIDPSBalance,
    });
    replace(mappedInvestments);
    const favouritedInvestmentsFiltered = filterFavouritedInvestments(
      originalInvestmentSelections || [],
    );
    setFavouritedInvestments(favouritedInvestmentsFiltered);
  };

  const handleTemplateReplaceFeeCalculators = (
    feeCalculatorsToReplace: PortfolioTemplateFeeCalculatorDataDTO[],
  ) => {
    const isPraemiumSpectrum = // TODO: Hardcode for Praemium Spectrum, need refactor
      portfolioSetup?.product?.name === GlobalProductNames.Praemium &&
      portfolioSetup?.name === GlobalSubProductNames.PraemiumSpectrum;

    const balanceAvailabilities: BalanceAvailabilitiesParams = {
      hasIDPSBalance,
      hasSuperBalance,
      ...(portfolioSetup || {}),
      cashAvailableInIDPS: true,
      cashAvailableInSuper: isPraemiumSpectrum ? false : true,
    };

    const updatedFeeCalculators = mapFeeCalculatorDataForTemplateReplacement({
      productName: portfolioSetup?.product?.name || "",
      templateFeeCalculatorData: feeCalculatorsToReplace,
      originalFeeCalculators: feeCalculators ?? [],
      investmentTypeSelection: showFeeCalculatorSection,
      balanceAvailabilities,
      total: calculatedTotal.total,
    });

    formMethod.setValue("feeCalculatorData", updatedFeeCalculators);
  };

  const handleOnTemplateReplaceData = (
    investments: SelectedInvestmentDTO[],
    feeCalculatorData: PortfolioTemplateFeeCalculatorDataDTO[],
  ) => {
    handleTemplateReplaceInvestments(investments);
    handleTemplateReplaceFeeCalculators(feeCalculatorData);
  };
  // endregion

  const renderTooltipTitle = () => {
    return (
      isSaveTemplateButtonDisabled && (
        <SHStack
          sx={{
            p: 1,
            mt: "3px",
            backgroundColor: `${hexToRGBA(
              palette.common.white,
              0.85,
            )} !important`,
            border: "1px solid #E3E3E3",
            backdropFilter: "blur(2px)",
            borderRadius: "3px",
            boxShadow: shadows[1],
            position: "relative",
          }}
        >
          <SHTypography
            variant="body3"
            sx={{
              p: "5px 5px 0px 10px",
            }}
          >
            {!templateRemainingValid && investmentWeightValid
              ? "Remaining allocation must equal zero."
              : "At least one model must have a weight."}
          </SHTypography>
        </SHStack>
      )
    );
  };

  useEffect(() => {
    if (!open) return;

    const isPraemiumSpectrum = // TODO: Hardcode for Praemium Spectrum, need refactor
      portfolioSetup?.product?.name === GlobalProductNames.Praemium &&
      portfolioSetup?.name === GlobalSubProductNames.PraemiumSpectrum;

    const balanceAvailabilities: BalanceAvailabilitiesParams = {
      hasIDPSBalance,
      hasSuperBalance,
      ...(portfolioSetup || {}),
      cashAvailableInIDPS: true,
      cashAvailableInSuper: isPraemiumSpectrum ? false : true,
    };

    // Set fee calculators
    const feeCalculatorList =
      type === "create"
        ? getCreateFeeCalculatorsByVariables({
            productName: portfolioSetup?.product?.name || "",
            subProductName: portfolioSetup?.name || "",
            feeCalculators: feeCalculators || [],
            investmentTypeSelection: showFeeCalculatorSection,
            balanceAvailabilities,
          })
        : getEditFeeCalculatorsByVariables({
            productName: portfolioSetup?.product?.name || "",
            subProductName: portfolioSetup?.name || "",
            updatedFeeCalculators: mapFeeCalculatorsDataToFeeCalculator(
              portfolio?.feeCalculatorData ?? [],
              feeCalculators ?? [],
            ),
            investmentTypeSelection: showFeeCalculatorSection,
            balanceAvailabilities,
          });

    formMethod.setValue(
      "feeCalculatorData",
      mapFeeCalculatorsToFeeCalculatorsData(
        feeCalculatorList,
        calculatedTotal.total,
      ),
    );

    setInitialFormData((prev) => ({
      ...prev,
      feeCalculatorData: mapFeeCalculatorsToFeeCalculatorsData(
        feeCalculatorList,
        calculatedTotal.total,
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (type === "create") {
      toggleIsInvestmentLoading(false);
      return;
    }

    if (!open || !investmentSelections) return;

    const formDataSelectedInvestments = getValues("selectedInvestments") || [];
    const currentSelectedInvestments = isEmpty(formDataSelectedInvestments)
      ? portfolio?.selectedInvestments ?? []
      : formDataSelectedInvestments;
    const selectedInvestmentsMapped = mapInvestmentPortfolioForEdit({
      selectedInvestments: currentSelectedInvestments,
      allInvestments: investmentSelections,
      total: calculatedTotal.total,
      hasSuperBalance,
      hasIDPSBalance,
    });
    const sortedInvestments = sortBy(selectedInvestmentsMapped, [
      "displayName",
    ]);

    formMethod.setValue("selectedInvestments", sortedInvestments);
    toggleIsInvestmentLoading(false);

    setInitialFormData((prev) => ({
      ...prev,
      selectedInvestments: selectedInvestmentsMapped,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, investmentSelections, type]);

  return portfolioSetup && !!type ? (
    <SHDialog
      open={open}
      disableBackdropClick={true}
      onClose={handleOnModalClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "1000px",
          minWidth: "770px",
          height: "90vh",
          minHeight: "600px",
          padding: "0px 0px",
          position: "relative",
        },
      }}
    >
      <SHStack
        display={isLoading || isEmpty(selectedFeeCalculators) ? "flex" : "none"}
        height={"100%"}
        width={"100%"}
      >
        <PortfolioSetupModalSkeleton />
      </SHStack>
      <SHStack
        height={"100%"}
        width={"100%"}
        display={
          !isLoading && !isEmpty(selectedFeeCalculators) ? "flex" : "none"
        }
      >
        <FormProvider {...formMethod}>
          {/* Dialog title */}
          <DialogTitle
            sx={{
              alignItems: "center",
              justifyItems: "center",
            }}
          >
            <SHStack>
              <SHTypography variant="h2" fontWeight={600}>
                Portfolio setup: {portfolioSetup?.product?.name}{" "}
                {!shouldHideSubProductName(
                  portfolioSetup.setupType,
                  portfolioSetup.product?.name,
                ) && portfolioSetup?.name}
              </SHTypography>
            </SHStack>
            <SHStack
              sx={{
                position: "absolute",
                top: "22px",
                right: "25px",
              }}
            >
              <CloseButton
                width="12px"
                height="12px"
                onClick={handleOnModalClose}
              />
            </SHStack>
          </DialogTitle>

          {/* Dialog content */}
          <DialogContent
            sx={{
              alignItems: "center",
              justifyItems: "center",
              overflowY: "hidden",
              position: "relative",
              height: "100%",
            }}
          >
            {/* Dropdowns */}
            <SHStack
              sx={{ backgroundColor: "" }}
              width={"100%"}
              height={"180px"}
              gap={1}
            >
              <SHStack
                sx={{ backgroundColor: "" }}
                direction={"row"}
                width={"100%"}
                gap={5}
              >
                <SHStack width={"50%"} gap={1} alignItems={"start"}>
                  <SHTypography variant={"subtitle2"}>
                    Portfolio composition
                  </SHTypography>

                  <SHTypography
                    variant="body4"
                    color={palette.text.third}
                    textAlign={"left"}
                  >
                    You can either choose each investment to include in your
                    analysis one by one, or you can load from a template you or
                    someone from your firm has created
                  </SHTypography>
                </SHStack>

                <SHStack
                  width={"50%"}
                  sx={{
                    "& .MuiAutocomplete-root": {
                      width: "100%",
                    },
                  }}
                >
                  <PortfolioTemplate
                    portfolioSize={calculatedTotal.total}
                    onReplaceData={handleOnTemplateReplaceData}
                    onClearSaveAsTemplateWarnings={() => {
                      /* no-op */
                    }}
                    onLoaded={() => toggleIsTemplateLoading(false)}
                  />
                </SHStack>
              </SHStack>

              <SHStack
                sx={{ backgroundColor: "" }}
                direction={"row"}
                justifyContent={"end"}
                width={"100%"}
                gap={5}
              >
                <SHStack width={"50%"} gap={1}>
                  <SelectInvestments
                    disabled={
                      isSubmitting ||
                      selectedInvestments?.length >=
                        StepConfigs.MaximumPortfolioSelectedInvestments
                    }
                    isGettingPlatformAnalysisInvestments
                    subProductId={
                      portfolioSetup.setupType ===
                      AnalysisFeePortfolioSetupType.RateCard
                        ? portfolioSetup?.subProductId ?? undefined
                        : portfolioSetup?.id
                    }
                    platformAnalysisId={analysisId}
                    textFieldProps={{
                      InputLabelProps: { shrink: true },
                      label: "Add investments to the portfolio",
                      placeholder: "Search all investments",
                      inputRef: inputRef,
                    }}
                    calculatedTotal={calculatedTotal}
                    openOnFocus={true}
                    onLoaded={handleOnSelectInvestmentLoaded}
                    selectedCodes={selectedInvestments?.map((i) => i.shCode)}
                    onAppend={handleAppendInvestment}
                    onRemove={handleRemoveInvestment}
                    onRemoveAll={handleRemoveAllInvestments}
                  />
                </SHStack>
                <SHStack width={"50%"}>
                  {favouritedInvestments && (
                    <SelectInvestmentsFavourited
                      disabled={
                        isSubmitting ||
                        selectedInvestments?.length >=
                          StepConfigs.MaximumPortfolioSelectedInvestments
                      }
                      initialData={favouritedInvestments}
                      textFieldProps={{
                        InputLabelProps: { shrink: true },
                        label: "Select from favourites",
                        placeholder: "Search favourited investments",
                      }}
                      popperPlacementAlwaysOnTop={false}
                      onLoaded={() =>
                        toggleIsFavouritedInvestmentLoading(false)
                      }
                      selectedCodes={selectedInvestments?.map((i) => i.shCode)}
                      onAppend={handleAppendInvestment}
                      onRemove={handleRemoveInvestment}
                      onRemoveAll={handleRemoveAllFavouritedInvestments}
                    />
                  )}
                </SHStack>
              </SHStack>
            </SHStack>

            {/* Table */}
            <SHStack
              direction={"column"}
              height={"calc(100% - 250px)"}
              width={"100%"}
              zIndex={5}
            >
              <PortfolioSetupTableWrapper>
                <SHTable stickyHeader>
                  <SHTableHead>
                    <SHTableRow>
                      <SHTableCell>
                        <SHStack paddingLeft={"80px"}>
                          <SHTypography variant="body1">
                            Investment name/code
                          </SHTypography>
                        </SHStack>
                      </SHTableCell>

                      <SHTableCell>
                        <SHTypography variant="body1">Weight</SHTypography>
                      </SHTableCell>

                      <SHTableCell>
                        <SHTypography variant="body1" paddingLeft={0.5}>
                          Value
                        </SHTypography>
                      </SHTableCell>
                    </SHTableRow>
                  </SHTableHead>

                  <SHTableBody>
                    {/* Render fee calculators */}
                    {selectedFeeCalculators?.map((item, index) => {
                      return (
                        <FeeCalculatorRow
                          key={item.variableName}
                          disabled={isSubmitting}
                          item={item}
                          itemIndex={index}
                          calculatedTotal={calculatedTotal.total}
                          onDelete={() => handleRemoveFeeCalculator(index)}
                        />
                      );
                    })}

                    {/* Render selected investments */}
                    {selectedInvestments?.map((item, index) => {
                      return (
                        <AnalysisFeePortfolioSetupInvestmentRow
                          key={item.shCode}
                          disabled={isSubmitting}
                          item={item}
                          itemIndex={index}
                          calculatedTotal={calculatedTotal.total}
                          onDelete={() => {
                            handleRemoveInvestment(item.shCode);
                          }}
                          onFavourited={(isFavourited: boolean) => {
                            handleOnInvestmentRowFavouritedValueChange(
                              item,
                              isFavourited,
                            );
                          }}
                        />
                      );
                    })}
                  </SHTableBody>
                </SHTable>
              </PortfolioSetupTableWrapper>
              <SHStack
                direction={"row"}
                zIndex={10}
                marginTop={1}
                width={"100%"}
                gap={2}
              >
                <PlusButton
                  variant="contained"
                  size="medium"
                  onClick={() => {
                    inputRef.current?.focus();
                  }}
                >
                  Investments
                </PlusButton>
                <TooltipStyled
                  placement="bottom"
                  enterDelay={DELAY_TIME}
                  enterNextDelay={DELAY_TIME}
                  title={renderTooltipTitle()}
                >
                  <Collapse in={selectedInvestments.length > 0}>
                    <SHButton
                      disabled={isSaveTemplateButtonDisabled}
                      isLoading={isLoading}
                      variant={"outlined"}
                      size={"medium"}
                      onClick={() => {
                        setShowSaveAsTemplateDialog(true);
                      }}
                    >
                      Save as template
                    </SHButton>
                  </Collapse>
                </TooltipStyled>
              </SHStack>
            </SHStack>
          </DialogContent>

          {/* Dialog actions */}
          <DialogActions
            sx={{
              height: 130,
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              alignItems: "center",
              justifyItems: "center",
              background: "white",
              zIndex: 1,
              padding: 2,
            }}
          >
            <SHStack width={"100%"} gap={1} marginRight={1}>
              {/* Sum */}
              <AnalysisFeePortfolioSetupRemainingCalculator
                selectedFeeCalculators={selectedFeeCalculators}
                selectedInvestments={selectedInvestments}
                calculatedTotal={calculatedTotal.total}
                onValidationChange={(
                  isRemainingValid,
                  isInvestmentAllocationValid,
                  isMissingInvestment,
                  isTemplateRemainingValid,
                  isInvestmentValid,
                ) => {
                  toggleRemainingValid(isRemainingValid);
                  toggleAllocationValid(isInvestmentAllocationValid);
                  toggleIsMissingInvestment(isMissingInvestment);
                  toggleTemplateRemainingValid(isTemplateRemainingValid);
                  toggleInvestmentWeightValid(isInvestmentValid);
                }}
              />

              {/* Buttons */}
              {type === "create" ? (
                <SHStack
                  width={"100%"}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  paddingBottom={2}
                  gap={2}
                >
                  <SHButton onClick={handleOnModalClose} size={"extraMedium"}>
                    Cancel
                  </SHButton>

                  <SHButton
                    disabled={isSaveButtonDisabled}
                    variant={"contained"}
                    size={"extraMedium"}
                    onClick={() => {
                      toggleIsSubmitting(true);
                      setTimeout(() => {
                        handleOnSavePortfolio().then(() =>
                          toggleIsSubmitting(false),
                        );
                      }, 0);
                    }}
                    isLoading={isSubmitting}
                  >
                    Save portfolio
                  </SHButton>
                </SHStack>
              ) : (
                <SHStack
                  width={"100%"}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  paddingBottom={1}
                  gap={2}
                >
                  <SHButton onClick={handleOnModalClose} size={"extraMedium"}>
                    Cancel
                  </SHButton>
                  <SHButton
                    disabled={isSaveButtonDisabled}
                    variant={"outlined"}
                    size={"extraMedium"}
                    onClick={() => {
                      toggleIsSubmitting(true);
                      setTimeout(() => {
                        handleOnOverwritePortfolio().then(() =>
                          toggleIsSubmitting(false),
                        );
                      }, 0);
                    }}
                  >
                    Overwrite portfolio
                  </SHButton>
                  <SHButton
                    disabled={isSaveButtonDisabled}
                    isLoading={isSubmitting}
                    variant={"contained"}
                    size={"extraMedium"}
                    onClick={() => {
                      toggleIsSubmitting(true);
                      setTimeout(() => {
                        handleOnSavePortfolio().then(() =>
                          toggleIsSubmitting(false),
                        );
                      }, 0);
                    }}
                  >
                    Save as new portfolio
                  </SHButton>
                </SHStack>
              )}
            </SHStack>
          </DialogActions>
        </FormProvider>
      </SHStack>
      {showSaveAsTemplateDialog && (
        <SaveInvestmentAsTemplateDialog
          onClose={() => setShowSaveAsTemplateDialog(false)}
          onSubmit={() => {
            /* no-op */
          }}
          selectedInvestments={selectedInvestments}
          feeCalculatorData={mapPortfolioFeeCalculatorsWithTemplates(
            feeCalculatorData,
          )}
        />
      )}
      <SHDialog
        disableEscapeKeyDown
        disableBackdropClick
        open={showDirtyPopup}
        onClose={onClose}
        width={400}
        header={"You have unsaved changes"}
        body={"Would you like to discard them?"}
        footer={
          <SHStack
            width="100%"
            direction="row"
            spacing={7}
            alignItems="center"
            justifyContent="center"
          >
            <SHButton
              color="secondary"
              onClick={onClose}
              variant="text"
              size="extraMedium"
            >
              Discard
            </SHButton>
            <SHButton
              color="primary"
              size="extraMedium"
              variant="contained"
              onClick={() => {
                toggleShowDirtyPopup(false);
              }}
            >
              Keep editing
            </SHButton>
          </SHStack>
        }
      />
    </SHDialog>
  ) : null;
};
